import { motion } from "framer-motion";
import { ArrowRight, Building2, Check, Heart } from "lucide-react";
import { Reveal } from "@/components/marketing/Reveal";

const scrollToContact = () => {
  window.dispatchEvent(new CustomEvent("dc:prefill-role", { detail: "Agency / Caseworker" }));
  document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
};

const CARDS = [
  {
    icon: Heart,
    tag: "For Parents & Social Workers",
    name: "No cost to parents.",
    blurb:
      "Every parent deserves bonding time with their child — and every caseworker deserves calm, clean records. Dual Care comes at no cost to the people doing the work.",
    features: [
      "Visit scheduling & approvals",
      "Punctuality & attendance logs",
      "Engagement & case notes",
      "Secure communication vault",
      "Case-ready PDF & CSV exports",
    ],
    cta: "Start Here",
    testid: "plans-start-here-cta",
    cardTestid: "plans-parents-card",
    external: true,
    featured: false,
  },
  {
    icon: Building2,
    tag: "Child Welfare Agencies",
    name: "Group rates for agencies.",
    blurb:
      "For child welfare agencies and casework teams guiding many families at once — priced per team, shaped around your caseload.",
    features: [
      "Multi-family dashboard",
      "Case-note review & assessment",
      "Team access & role management",
      "Onboarding & staff training",
      "Priority support",
    ],
    cta: "Request Group Rates",
    testid: "plans-agency-cta",
    cardTestid: "plans-agency-card",
    external: false,
    featured: true,
  },
];

const PlanCard = ({ card }) => {
  const cls = `mt-9 inline-flex w-full items-center justify-center gap-2 rounded-full py-3.5 font-medium transition-colors duration-300 ${
    card.featured ? "bg-clay text-paper hover:bg-clay-dark" : "bg-ink text-paper hover:bg-sage"
  }`;
  const inner = (
    <>
      {card.cta} <ArrowRight className="h-4 w-4" />
    </>
  );
  return (
    <div
      className={`relative h-full rounded-3xl p-9 sm:p-12 flex flex-col ${
        card.featured ? "bg-ink text-paper shadow-[0_40px_80px_-30px_rgba(26,25,24,0.45)]" : "bg-paper border border-ink/10"
      }`}
      data-testid={card.cardTestid}
    >
      <div className="flex items-center gap-2.5">
        <card.icon className={`h-5 w-5 ${card.featured ? "text-gold" : "text-clay"}`} />
        <span className={`font-mono text-[11px] uppercase tracking-[0.25em] font-semibold ${card.featured ? "text-gold" : "text-clay"}`}>
          {card.tag}
        </span>
      </div>
      <h3 className="mt-6 font-serif text-3xl sm:text-4xl font-semibold tracking-tight">{card.name}</h3>
      <p className={`mt-4 text-sm sm:text-base leading-relaxed ${card.featured ? "text-paper/70" : "text-ink-muted"}`}>{card.blurb}</p>
      <ul className="mt-8 space-y-3 flex-1">
        {card.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm">
            <Check className={`h-4 w-4 mt-0.5 shrink-0 ${card.featured ? "text-gold" : "text-sage"}`} />
            <span className={card.featured ? "text-paper/85" : "text-ink-muted"}>{f}</span>
          </li>
        ))}
      </ul>
      {card.external ? (
        <motion.a
          href="https://dualcare4u.com"
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          data-testid={card.testid}
          className={cls}
        >
          {inner}
        </motion.a>
      ) : (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={scrollToContact}
          data-testid={card.testid}
          className={cls}
        >
          {inner}
        </motion.button>
      )}
    </div>
  );
};

export default function Pricing() {
  return (
    <section id="plans" className="py-24 sm:py-32 bg-paper-2 border-y border-ink/10" data-testid="plans-section">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal className="text-center">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-clay font-semibold">Plans</p>
          <h2 className="mt-5 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            No cost to parents. Group rates for agencies.
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-base text-ink-muted leading-relaxed">
            Bonding time shouldn't have a paywall. Parents and social workers use Dual Care at no
            cost — child welfare agencies partner with us at group rates.
          </p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 gap-8 items-stretch">
          {CARDS.map((c, i) => (
            <Reveal key={c.tag} delay={i * 0.12} className="h-full">
              <PlanCard card={c} />
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-10 text-center text-xs text-ink-light font-mono" data-testid="plans-note">
            Group rates are tailored to your agency size — tell us about your team in the contact form below.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
